import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/TournamentManager.css';

// ─── Helpers ────────────────────────────────────────────────────────
const STORAGE_KEY = "laserbay_tournaments";

function loadTournaments() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveTournaments(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function generateRoundRobin(teams) {
  const list = [...teams];
  if (list.length % 2 !== 0) list.push(null);
  const rounds = [];
  const half = list.length / 2;
  for (let r = 0; r < list.length - 1; r++) {
    const pairs = [];
    for (let i = 0; i < half; i++) {
      const a = list[i];
      const b = list[list.length - 1 - i];
      if (a !== null && b !== null) pairs.push([a, b]);
    }
    rounds.push(pairs);
    list.splice(1, 0, list.pop());
  }
  return rounds;
}

function getStandings(tournament) {
  const map = {};
  tournament.teams.forEach(t => {
    map[t] = { name: t, wins: 0, losses: 0, draws: 0, pts: 0, played: 0, totalScore: 0 };
  });
  tournament.rounds.forEach(round => {
    round.matches.forEach(m => {
      if (m.scoreA === null || m.scoreB === null) return;
      const a = map[m.teamA];
      const b = map[m.teamB];
      if (!a || !b) return;
      a.played++; b.played++;
      a.totalScore += m.scoreA; b.totalScore += m.scoreB;
      if (m.scoreA > m.scoreB) { a.wins++; b.losses++; a.pts += 3; }
      else if (m.scoreA < m.scoreB) { b.wins++; a.losses++; b.pts += 3; }
      else { a.draws++; b.draws++; a.pts += 1; b.pts += 1; }
    });
  });
  return Object.values(map).sort((a, b) => b.pts - a.pts || b.totalScore - a.totalScore);
}

const TEAM_COLORS = [
  "#FF4136", "#2ECC40", "#0074D9", "#FF851B", "#B10DC9", "#FFDC00",
  "#01FF70", "#F012BE", "#39CCCC", "#FF6F61"
];

// ─── Score Input Component ──────────────────────────────────────────
function ScoreInput({ value, color, onConfirm }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = () => {
    setDraft(value !== null ? String(value) : "");
    setEditing(true);
  };

  const confirm = () => {
    const num = draft.trim() === "" ? null : parseInt(draft, 10);
    onConfirm(isNaN(num) ? null : Math.max(0, num));
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        autoFocus
        className="score-input-field"
        style={{ borderColor: color || '#3b82f6', boxShadow: `0 0 8px ${color || '#3b82f6'}40` }}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={confirm}
        onKeyDown={e => { if (e.key === "Enter") confirm(); }}
      />
    );
  }

  return (
    <div className="score-display-tap" onClick={startEdit}>
      {value !== null && value !== undefined ? value : "–"}
    </div>
  );
}

// ─── Main Tournament Manager ────────────────────────────────────────
function TournamentManager() {
  const [tournaments, setTournaments] = useState(() => loadTournaments());
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState("list"); // list | create | tournament

  useEffect(() => { saveTournaments(tournaments); }, [tournaments]);

  const active = useMemo(() => tournaments.find(t => t.id === activeId), [tournaments, activeId]);

  const createTournament = useCallback((name, teams) => {
    const rounds = generateRoundRobin(teams);
    const t = {
      id: Date.now().toString(),
      name,
      teams,
      createdAt: new Date().toISOString(),
      rounds: rounds.map((pairs, i) => ({
        num: i + 1,
        matches: pairs.map((p, j) => ({
          id: `${i}-${j}`,
          teamA: p[0],
          teamB: p[1],
          scoreA: null,
          scoreB: null
        }))
      }))
    };
    setTournaments(prev => [t, ...prev]);
    setActiveId(t.id);
    setView("tournament");
  }, []);

  const updateScore = useCallback((roundIdx, matchId, field, value) => {
    setTournaments(prev => prev.map(t => {
      if (t.id !== activeId) return t;
      const rounds = t.rounds.map((r, ri) => {
        if (ri !== roundIdx) return r;
        return { ...r, matches: r.matches.map(m => m.id === matchId ? { ...m, [field]: value } : m) };
      });
      return { ...t, rounds };
    }));
  }, [activeId]);

  const deleteTournament = useCallback((id) => {
    setTournaments(prev => prev.filter(t => t.id !== id));
    if (activeId === id) { setActiveId(null); setView("list"); }
  }, [activeId]);

  if (view === "create") {
    return <CreateView onCreate={createTournament} onBack={() => setView("list")} />;
  }
  if (view === "tournament" && active) {
    return (
      <TournamentView
        tournament={active}
        updateScore={updateScore}
        onBack={() => { setActiveId(null); setView("list"); }}
        onDelete={() => deleteTournament(active.id)}
      />
    );
  }

  return <ListView tournaments={tournaments} onNew={() => setView("create")} onOpen={(id) => { setActiveId(id); setView("tournament"); }} onDelete={deleteTournament} />;
}

// ─── List View ──────────────────────────────────────────────────────
function ListView({ tournaments, onNew, onOpen, onDelete }) {
  return (
    <div className="tournament-container">
      <div className="tournament-header-bar">
        <h2>🎯 Tournois Laser Tag</h2>
        <span className="offline-badge">⚡ Offline</span>
      </div>

      <button className="btn-new-tournament" onClick={onNew}>
        + Nouveau Tournoi
      </button>

      {tournaments.length === 0 ? (
        <div className="tournament-empty">
          <div className="empty-icon">🏆</div>
          <p>Aucun tournoi pour le moment</p>
          <p className="empty-sub">Crée ton premier tournoi laser tag !</p>
        </div>
      ) : (
        <div className="tournament-list">
          {tournaments.map(t => {
            const totalMatches = t.rounds.reduce((s, r) => s + r.matches.length, 0);
            const played = t.rounds.reduce((s, r) => s + r.matches.filter(m => m.scoreA !== null).length, 0);
            const pct = totalMatches > 0 ? Math.round((played / totalMatches) * 100) : 0;
            return (
              <div key={t.id} className="tournament-card" onClick={() => onOpen(t.id)}>
                <div className="card-top">
                  <div>
                    <h3>{t.name}</h3>
                    <p className="card-meta">{t.teams.length} équipes · {totalMatches} matchs</p>
                  </div>
                  <button className="btn-delete-small" onClick={e => { e.stopPropagation(); onDelete(t.id); }}>✕</button>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <p className="progress-label">{pct}% complété</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Create View ────────────────────────────────────────────────────
function CreateView({ onCreate, onBack }) {
  const [name, setName] = useState("");
  const [numTeams, setNumTeams] = useState(4);
  const [teamNames, setTeamNames] = useState([]);

  useEffect(() => {
    setTeamNames(Array.from({ length: numTeams }, (_, i) => `Équipe ${i + 1}`));
  }, [numTeams]);

  const updateTeamName = (i, v) => {
    setTeamNames(prev => prev.map((n, j) => j === i ? v : n));
  };

  const valid = name.trim() && teamNames.every(n => n.trim());
  const previewRounds = generateRoundRobin(teamNames).length;

  return (
    <div className="tournament-container">
      <button className="btn-back" onClick={onBack}>&lt; Retour aux tournois</button>
      <h2>Nouveau Tournoi</h2>

      <div className="form-section">
        <label>Nom du tournoi</label>
        <input
          type="text"
          className="form-input"
          placeholder="Ex: Tournoi Colo Juillet"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>

      <div className="form-section">
        <label>Nombre d'équipes</label>
        <div className="num-picker">
          {[3, 4, 5, 6].map(n => (
            <button
              key={n}
              className={`num-btn ${n === numTeams ? 'active' : ''}`}
              onClick={() => setNumTeams(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="form-section">
        <label>Noms des équipes</label>
        <div className="team-name-list">
          {teamNames.map((tn, i) => (
            <div key={i} className="team-name-row">
              <span className="team-color-dot" style={{ background: TEAM_COLORS[i] }} />
              <input
                type="text"
                className="form-input"
                value={tn}
                onChange={e => updateTeamName(i, e.target.value)}
                placeholder={`Équipe ${i + 1}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="info-box">
        📋 <strong>{previewRounds} manches</strong> seront générées — chaque équipe affronte toutes les autres.
      </div>

      <button
        className="btn-new-tournament"
        disabled={!valid}
        onClick={() => valid && onCreate(name.trim(), teamNames.map(n => n.trim()))}
      >
        🚀 Lancer le tournoi
      </button>
    </div>
  );
}

// ─── Tournament View ────────────────────────────────────────────────
function TournamentView({ tournament, updateScore, onBack, onDelete }) {
  const [tab, setTab] = useState("matches");
  const [activeRound, setActiveRound] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const standings = useMemo(() => getStandings(tournament), [tournament]);
  const teamColorMap = useMemo(() => {
    const m = {};
    tournament.teams.forEach((t, i) => { m[t] = TEAM_COLORS[i % TEAM_COLORS.length]; });
    return m;
  }, [tournament.teams]);

  const totalMatches = tournament.rounds.reduce((s, r) => s + r.matches.length, 0);
  const played = tournament.rounds.reduce((s, r) => s + r.matches.filter(m => m.scoreA !== null).length, 0);
  const isComplete = played === totalMatches && totalMatches > 0;

  return (
    <div className="tournament-container">
      <div className="tournament-top-bar">
        <button className="btn-back" onClick={onBack}>&lt; Tournois</button>
        <button className="btn-delete-small" onClick={() => setConfirmDelete(true)}>🗑️ Supprimer</button>
      </div>

      <h2>{tournament.name}</h2>
      <p className="tournament-meta">{tournament.teams.length} équipes · {played}/{totalMatches} matchs joués</p>

      {isComplete && (
        <div className="winner-banner">
          🏆 <strong>Vainqueur : {standings[0]?.name}</strong> 🏆
        </div>
      )}

      {/* Tabs */}
      <div className="tournament-tabs">
        <button className={`tab-btn ${tab === "matches" ? 'active' : ''}`} onClick={() => setTab("matches")}>
          ⚔️ Matchs
        </button>
        <button className={`tab-btn ${tab === "standings" ? 'active' : ''}`} onClick={() => setTab("standings")}>
          🏅 Classement
        </button>
      </div>

      {tab === "matches" && (
        <>
          {/* Round selector */}
          <div className="round-selector">
            {tournament.rounds.map((r, i) => {
              const done = r.matches.every(m => m.scoreA !== null);
              return (
                <button
                  key={i}
                  className={`round-btn ${i === activeRound ? 'active' : ''}`}
                  onClick={() => setActiveRound(i)}
                >
                  M{r.num} {done && <span className="round-check">✓</span>}
                </button>
              );
            })}
          </div>

          {/* Matches */}
          <div className="match-list">
            {tournament.rounds[activeRound]?.matches.map((m) => (
              <div key={m.id} className="match-card">
                <div className="match-team">
                  <span className="team-badge" style={{ background: teamColorMap[m.teamA] }}>
                    {m.teamA.charAt(0)}
                  </span>
                  <span className="team-name">{m.teamA}</span>
                </div>
                <div className="score-zone">
                  <ScoreInput
                    value={m.scoreA}
                    color={teamColorMap[m.teamA]}
                    onConfirm={v => updateScore(activeRound, m.id, "scoreA", v)}
                  />
                  <span className="vs-label">VS</span>
                  <ScoreInput
                    value={m.scoreB}
                    color={teamColorMap[m.teamB]}
                    onConfirm={v => updateScore(activeRound, m.id, "scoreB", v)}
                  />
                </div>
                <div className="match-team">
                  <span className="team-badge" style={{ background: teamColorMap[m.teamB] }}>
                    {m.teamB.charAt(0)}
                  </span>
                  <span className="team-name">{m.teamB}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "standings" && (
        <table className="standings-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Équipe</th>
              <th>J</th>
              <th>V</th>
              <th>N</th>
              <th>D</th>
              <th>Score</th>
              <th>Pts</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((s, i) => (
              <tr key={s.name} className={i === 0 && isComplete ? 'winner-row' : ''}>
                <td className="rank-cell">
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                </td>
                <td>
                  <span className="team-color-dot" style={{ background: teamColorMap[s.name] }} />
                  {s.name}
                </td>
                <td>{s.played}</td>
                <td className="win-cell">{s.wins}</td>
                <td>{s.draws}</td>
                <td className="loss-cell">{s.losses}</td>
                <td className="score-cell">{s.totalScore}</td>
                <td className="pts-cell">{s.pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Supprimer ce tournoi ?</h3>
            <p>"{tournament.name}" sera définitivement supprimé.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setConfirmDelete(false)}>Annuler</button>
              <button className="confirm-button" onClick={onDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentManager;
